import * as React from 'react';
import { IBaseQuestionProps, TAnswersState } from '../FormContainer/types/internal';
import { QuestionLayout } from '../../components/QuestionLayout';
import { pathOr } from 'ramda';
import { useSimpleForm } from '../../utils/useSimpleForm';
import { PriceQuestion } from '../../components/PriceQuestion';
import { isEmptyString, isValidNumber } from '../../utils/validators';
import { formatPrice, getNumberString, usePriceOptions, validatePrice } from './utils';
import { DEFAULT_PRICE_PLACEHOLDER, PRICE_VALUE_FIELD } from './constants';
import { PriceEstimationContainer } from '../PriceEstimationContainer';
import { pickOnlyMeaning } from '../../utils/pickOnlyMeaning';
import { useFormTracking } from '../../utils/useFormTracking';

export const PriceQuestionContainer: React.FC<React.PropsWithChildren<IBaseQuestionProps>> = props => {
  const {
    question: { answerField, placeholder, title, unit, required },
    answersState,
    setNextStep,
    setAnswersState,
  } = props;

  const { trackSubmit, trackChange } = useFormTracking();

  const { selectedOptions, options, handleSelect } = usePriceOptions(props);

  const {
    handleSubmit,
    handleChange: onChange,
    values,
    errors,
    isSubmitDisabled,
  } = useSimpleForm<string>({
    onSubmit(values) {
      setAnswersState(prevState => {
        const price = values[PRICE_VALUE_FIELD] ? Number(getNumberString(values[PRICE_VALUE_FIELD])) : null;

        trackSubmit({
          isRequired: required,
          field: answerField,
          value: `${price}`,
        });

        const nextAnswersState: TAnswersState = {
          ...prevState,
          [answerField]: {
            answerField,
            value: {
              [PRICE_VALUE_FIELD]: price,
              options: selectedOptions,
            },
          },
        };

        setNextStep(nextAnswersState);

        return nextAnswersState;
      });
    },
    validators: {
      [PRICE_VALUE_FIELD]: value => validatePrice(value, required),
    },
    initialValues: pickOnlyMeaning({
      [PRICE_VALUE_FIELD]: formatPrice(pathOr('', [answerField, 'value', PRICE_VALUE_FIELD], answersState)),
    }),
    required,
  });

  const handleChange = React.useCallback(
    (value: string) => {
      trackChange();

      const numberValue = getNumberString(value);

      if (isEmptyString(value)) {
        return onChange(PRICE_VALUE_FIELD, value);
      }

      if (isValidNumber(numberValue)) {
        return onChange(PRICE_VALUE_FIELD, formatPrice(numberValue));
      }
    },
    [onChange, trackChange],
  );

  return (
    <QuestionLayout
      title={title}
      onSubmit={handleSubmit}
      footer={<PriceEstimationContainer />}
      disableSubmit={isSubmitDisabled}
    >
      <PriceQuestion
        value={pathOr('', [PRICE_VALUE_FIELD], values)}
        onValueChange={handleChange}
        placeholder={placeholder || DEFAULT_PRICE_PLACEHOLDER}
        onSelect={handleSelect}
        error={errors[PRICE_VALUE_FIELD]}
        selectedOptions={selectedOptions}
        options={options}
        unit={unit}
        inputName={answerField}
      />
    </QuestionLayout>
  );
};

PriceQuestionContainer.displayName = 'PriceQuestionContainer';
